import { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Clickable from 'components/Common/Clickable'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import getVoucherProductTypeIcon from 'lib/voucher/getVoucherProductTypeIcon'
import getVoucherTitleColour from 'lib/voucher/getVoucherTitleColour'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import { useLocation } from 'react-router'
import ModalContext from 'contexts/ModalContext'
import VoucherDetailsModal from 'components/Pages/VouchersPage/VoucherDetailsModal'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { VoucherDisplayConfig as VoucherDisplayConfigType, VoucherDisplayConfigVoucher } from 'api/promo'
import {
  getVoucherDisplayConfigs,
  getClaimedVoucherDisplayConfigVouchers,
  claimVoucherDisplayConfig,
} from 'api/promo'

function matchUrlPath(pattern: string, pathname: string): boolean {
  if (pattern.endsWith('/*')) {
    return pathname.startsWith(pattern.slice(0, -1))
  }
  return pathname === pattern
}

const MOBILE_NOTCH_DIAMETER = 12
const MOBILE_NOTCH_RADIUS = MOBILE_NOTCH_DIAMETER / 2
const MOBILE_NOTCH_X = 56

const DESKTOP_NOTCH_DIAMETER = 16
const DESKTOP_NOTCH_RADIUS = DESKTOP_NOTCH_DIAMETER / 2
const DESKTOP_NOTCH_X = 64

const VoucherCardMobile = styled(Group)`
  position: relative;
  padding: ${rem(12)} ${rem(10)} ${rem(12)} ${rem(12)};
  width: ${rem(300)};
  height: ${rem(80)};
  flex-shrink: 0;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: ${rem(MOBILE_NOTCH_DIAMETER)};
    height: ${rem(MOBILE_NOTCH_RADIUS)};
    left: ${rem(MOBILE_NOTCH_X)};
    transform: translateX(-50%);
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    z-index: 1;
  }

  &::before {
    top: -1px;
    border-bottom-left-radius: ${rem(MOBILE_NOTCH_RADIUS + 1)};
    border-bottom-right-radius: ${rem(MOBILE_NOTCH_RADIUS + 1)};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-top: none;
  }

  &::after {
    bottom: -1px;
    border-top-left-radius: ${rem(MOBILE_NOTCH_RADIUS + 1)};
    border-top-right-radius: ${rem(MOBILE_NOTCH_RADIUS + 1)};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-bottom: none;
  }
`

const VoucherCardDesktop = styled(Group)`
  position: relative;
  padding: ${rem(12)} ${rem(16)};
  width: ${rem(420)};
  height: ${rem(115)};
  flex-shrink: 0;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: ${rem(DESKTOP_NOTCH_DIAMETER)};
    height: ${rem(DESKTOP_NOTCH_RADIUS)};
    left: ${rem(DESKTOP_NOTCH_X)};
    transform: translateX(-50%);
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    z-index: 1;
  }

  &::before {
    top: -1px;
    border-bottom-left-radius: ${rem(DESKTOP_NOTCH_RADIUS + 1)};
    border-bottom-right-radius: ${rem(DESKTOP_NOTCH_RADIUS + 1)};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-top: none;
  }

  &::after {
    bottom: -1px;
    border-top-left-radius: ${rem(DESKTOP_NOTCH_RADIUS + 1)};
    border-top-right-radius: ${rem(DESKTOP_NOTCH_RADIUS + 1)};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-bottom: none;
  }
`

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${props => props.theme.palette.highlight.secondary.normalBackground};

  &.theme-luxplus {
    background-color: ${props => props.theme.palette.product.luxPlus.foreground};
  }

  &.theme-societe {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
  }
`

const TextWrapper = styled(Group)`
  min-width: 0;
`

const MobileDottedDivider = styled.div`
  height: ${rem(56)};
  width: 0;
  flex-shrink: 0;
  border-left: 2px dashed ${props => props.theme.palette.neutral.surface.lightForeground};
`

const DesktopDottedDivider = styled.div`
  height: ${rem(92)};
  width: 0;
  flex-shrink: 0;
  border-left: 2px dashed ${props => props.theme.palette.neutral.surface.lightForeground};
`

function VoucherDisplayConfig() {
  const isEnabled = config.ENABLE_VOUCHER_DISPLAY_CONFIG
  const intl = useIntl()
  const queryClient = useQueryClient()
  const showModal = useContext(ModalContext)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const isLoggedIn = !!useAppSelector(state => state.auth.account.memberId)
  const { pathname } = useLocation()
  const pathnameWithoutRegion = pathname.replace(/^\/[a-z]{2}\//i, '/')
  const utmSource = useAppSelector(state => state.utm.source)
  const utmMedium = useAppSelector(state => state.utm.medium)
  const utmCampaign = useAppSelector(state => state.utm.campaign)

  const { data: configs = [] } = useQuery({
    queryKey: ['voucher-display-configs', region],
    queryFn: () => getVoucherDisplayConfigs({ region }),
    enabled: isEnabled,
  })

  const { data: claimedVoucherIds = [] } = useQuery({
    queryKey: ['claimed-voucher-display-config-vouchers', region],
    queryFn: () => getClaimedVoucherDisplayConfigVouchers({ region }),
    enabled: isEnabled && isLoggedIn,
  })

  const claimMutation = useMutation({
    mutationFn: ({ voucherDisplayConfigId, voucherId }: { voucherDisplayConfigId: string; voucherId: string }) =>
      claimVoucherDisplayConfig(voucherDisplayConfigId, voucherId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claimed-voucher-display-config-vouchers'] })
    },
  })

  const handleClaim = useCallback((voucherDisplayConfigId: string, voucherId: string) => {
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return
    }
    claimMutation.mutate({ voucherDisplayConfigId, voucherId })
  }, [isLoggedIn, showModal, claimMutation])

  const handleDetailsClick = useCallback((voucher: VoucherDisplayConfigVoucher) => {
    showModal(<VoucherDetailsModal title={voucher.voucherTitle} description={voucher.voucherDescription} />)
  }, [showModal])

  const matchedConfig = configs.find(cfg => {
    const urlMatch = cfg.urlPaths.some(pattern => matchUrlPath(pattern, pathnameWithoutRegion))
    if (!urlMatch) return false
    if (cfg.source && cfg.source !== utmSource) return false
    if (cfg.medium && cfg.medium !== utmMedium) return false
    if (cfg.campaign && cfg.campaign !== utmCampaign) return false
    return true
  })

  const allVouchers: Array<{ config: VoucherDisplayConfigType; voucher: VoucherDisplayConfigVoucher }> = matchedConfig
    ? matchedConfig.vouchers.map(v => ({ config: matchedConfig, voucher: v }))
    : []

  if (allVouchers.length === 0) {
    return null
  }

  const claimLabel = intl.formatMessage({
    id: 'zfbRgZ-tl2pnCiCVfvu2',
    defaultMessage: 'Claim',
    description: 'Button label to claim a reward voucher in the available rewards section',
  })

  return (
    <LayoutContainer size="xl" style={{ paddingTop: rem(40), paddingBottom: rem(24) }}>
      <Group direction="vertical" gap={20}>
        <Heading variant="heading2" as="h2">
          <FormattedMessage
            id="W2ep2lxHH_jAirqkQBi8"
            defaultMessage="Available <italic>rewards</italic>"
            description="Section heading for available reward vouchers on the metasearch page"
            values={{
              italic: (chunks) => <em key="rewards-italic">{chunks}</em>,
            }}
          />
        </Heading>

        <CSSBreakpoint max="mobile">
          <HorizontalContainerWithFadedOverflow gap={12}>
            {allVouchers.map(({ config: cfg, voucher }) => {
              const isClaimed = claimedVoucherIds.includes(voucher.voucherId)
              const VoucherIcon = getVoucherProductTypeIcon(voucher.productType)
              const titleColour = getVoucherTitleColour(voucher.theme)

              return (
                <VoucherCardMobile key={voucher.voucherDisplayConfigVoucherId} direction="horizontal" gap={12} verticalAlign="center">
                  <IconCircle className={voucher.theme && voucher.theme !== 'default' ? `theme-${voucher.theme}` : undefined}>
                    <VoucherIcon size={20} colour="neutral-eight" />
                  </IconCircle>
                  <MobileDottedDivider />
                  <TextWrapper direction="vertical" gap={2} fullWidth>
                    <BodyText variant="small" colour={titleColour} wrap="truncate">{voucher.voucherName}</BodyText>
                    <BodyText variant="medium" weight="bold" colour="neutral-two" wrap="truncate">
                      {voucher.voucherTitle}
                    </BodyText>
                    <Group direction="horizontal" gap={4} verticalAlign="center">
                      <Clickable onClick={() => handleDetailsClick(voucher)}>
                        <Caption variant="medium" colour="neutral-two">
                          <Group direction="horizontal" gap={4} verticalAlign="center">
                            <Caption variant="medium" colour="neutral-two" underline>
                              {intl.formatMessage({
                                id: 'UT4BQ3uROjMjPlVz42pB',
                                defaultMessage: 'Details',
                                description: 'Link text to view voucher details in the available rewards section',
                              })}
                            </Caption>
                            <LineInfoCircleIcon size={16} colour="neutral-three" />
                          </Group>
                        </Caption>
                      </Clickable>
                    </Group>
                  </TextWrapper>
                  {!isClaimed && <AssistChip
                    variant="filled"
                    size="small"
                    onClick={() => handleClaim(cfg.voucherDisplayConfigId, voucher.voucherId)}
                    disabled={claimMutation.isPending}
                  >
                    {claimLabel}
                  </AssistChip>}
                  {isClaimed && <LineAngleRightIcon size="L" colour="neutral-two" />}
                </VoucherCardMobile>
              )
            })}
          </HorizontalContainerWithFadedOverflow>
        </CSSBreakpoint>

        <CSSBreakpoint min="tablet">
          <HorizontalContainerWithFadedOverflow gap={20}>
            {allVouchers.map(({ config: cfg, voucher }) => {
              const isClaimed = claimedVoucherIds.includes(voucher.voucherId)
              const VoucherIcon = getVoucherProductTypeIcon(voucher.productType)
              const titleColour = getVoucherTitleColour(voucher.theme)

              return (
                <VoucherCardDesktop key={voucher.voucherDisplayConfigVoucherId} direction="horizontal" gap={16} verticalAlign="center">
                  <IconCircle className={voucher.theme && voucher.theme !== 'default' ? `theme-${voucher.theme}` : undefined}>
                    <VoucherIcon size={20} colour="neutral-eight" />
                  </IconCircle>
                  <DesktopDottedDivider />
                  <TextWrapper direction="vertical" gap={4} fullWidth>
                    <BodyText variant="medium" colour={titleColour} wrap="truncate">{voucher.voucherName}</BodyText>
                    <BodyText variant="large" weight="bold" colour="neutral-two" wrap="truncate">
                      {voucher.voucherTitle}
                    </BodyText>
                    <Group direction="horizontal" gap={4}>
                      <Clickable onClick={() => handleDetailsClick(voucher)}>
                        <Caption variant="medium" colour="neutral-two" underline>
                          {intl.formatMessage({
                            id: 'UT4BQ3uROjMjPlVz42pB',
                            defaultMessage: 'Details',
                            description: 'Link text to view voucher details in the available rewards section',
                          })}
                        </Caption>
                      </Clickable>
                    </Group>
                  </TextWrapper>
                  {!isClaimed && <AssistChip
                    variant="filled"
                    size="medium"
                    onClick={() => handleClaim(cfg.voucherDisplayConfigId, voucher.voucherId)}
                    disabled={claimMutation.isPending}
                  >
                    {claimLabel}
                  </AssistChip>}
                  {isClaimed && <LineAngleRightIcon size="L" colour="neutral-two" />}
                </VoucherCardDesktop>
              )
            })}
          </HorizontalContainerWithFadedOverflow>
        </CSSBreakpoint>
      </Group>
    </LayoutContainer>
  )
}

export default VoucherDisplayConfig
